import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2, CornerDownLeft } from "lucide-react";
import { studio, eur } from "@/lib/studioApi";
import { StatusBadge } from "./StudioUI";

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setQ("");
    setGroups([]);
    setCursor(0);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") close();
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("devisto:open-search", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("devisto:open-search", onOpen);
    };
  }, [close]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 40);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (term.length < 2) {
      setGroups([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      studio
        .get("/search", { params: { q: term } })
        .then(({ data }) => {
          setGroups(data.groups || []);
          setCursor(0);
        })
        .catch(() => setGroups([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(timer);
  }, [q, open]);

  const flat = groups.flatMap((g) => g.items.map((it) => ({ ...it, group: g.key })));

  const go = (item) => {
    close();
    navigate(item.url);
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter" && flat[cursor]) {
      e.preventDefault();
      go(flat[cursor]);
    }
  };

  if (!open) return null;

  let index = -1;

  return (
    <div
      data-testid="global-search-dialog"
      className="fixed inset-0 z-[70] flex items-start justify-center bg-[#0F2645]/45 px-4 pt-[12vh] backdrop-blur-sm"
      onClick={close}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-2xl border border-white/10 bg-white shadow-[0_24px_60px_-20px_rgba(11,30,61,0.5)]"
        style={{ animation: "devFadeUp .16s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-[#E2E7F7] px-4">
          <Search className="h-4 w-4 shrink-0 text-[#93A0BC]" />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Client, dossier, devis, facture…"
            className="min-h-[52px] flex-1 bg-transparent text-sm text-[#0F2645] outline-none placeholder:text-[#93A0BC]"
          />
          {loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#3A5CC8]" />}
          <button
            type="button"
            data-testid="global-search-close"
            onClick={close}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#93A0BC] hover:bg-slate-100 hover:text-[#0F2645]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[52vh] overflow-y-auto px-2 py-2">
          {q.trim().length < 2 && (
            <p className="px-3 py-6 text-xs leading-relaxed text-[#5C6A87]">
              Tapez au moins 2 lettres. La recherche couvre vos clients, dossiers chantier, devis, interventions et
              factures.
            </p>
          )}
          {q.trim().length >= 2 && !loading && flat.length === 0 && (
            <p data-testid="global-search-empty" className="px-3 py-6 text-xs text-[#5C6A87]">
              Aucun résultat pour « {q.trim()} ».
            </p>
          )}
          {groups.map((g) => (
            <div key={g.key} className="mb-1">
              <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-[#93A0BC]">{g.label}</p>
              {g.items.map((item) => {
                index += 1;
                const active = index === cursor;
                return (
                  <button
                    key={`${g.key}-${item.id}`}
                    type="button"
                    data-testid={`search-result-${g.key}-${item.id}`}
                    onMouseEnter={() => setCursor(flat.findIndex((f) => f.id === item.id && f.group === g.key))}
                    onClick={() => go(item)}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                      active ? "bg-[#EEF1FC]" : "hover:bg-[#F6F8FD]"
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-[#0F2645]">{item.title}</span>
                        {item.status && <StatusBadge value={item.status} />}
                      </span>
                      {item.subtitle && <span className="block truncate text-xs text-[#5C6A87]">{item.subtitle}</span>}
                    </span>
                    {item.amount != null && (
                      <span className="shrink-0 text-sm font-bold tabular-nums text-[#0F2645]">{eur(item.amount)}</span>
                    )}
                    {active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-[#3A5CC8]" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
